import { Alert, Button } from "@/components";
import { useModal } from "@/contexts";
import { usePaymentRoomValue, useSetPaidMethodRecoilState } from "@/shared/client";
import { useGetAccount } from "@/shared/server";
import { Flex } from "@chakra-ui/react";
import styled from "@emotion/styled";
import { yupResolver } from "@hookform/resolvers/yup";
import { validateSchemas } from "@shared/validation";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { PayerInfo, Terms } from "./components";
import { usePayment } from "./hooks";
import { PaymentFormInputProps } from "./types";

export const PaymentForm = () => {
    const { setModalContent, openModal, setIconName } = useModal();
    const { onSubmitHandler } = usePayment();
    const { data } = useGetAccount();
    const setPaidMethodState = useSetPaidMethodRecoilState();

    const paymentRoom = usePaymentRoomValue();
    const totalPrice = useMemo(() => {
        return (
            paymentRoom.reduce((acc, item) => acc + item.finalPrice, 0) -
            paymentRoom.reduce((acc, item) => acc + item.couponDiscount, 0)
        );
    }, [paymentRoom]);

    const method = useForm<PaymentFormInputProps>({
        resolver: yupResolver(validateSchemas),
        defaultValues: {
            buyer: "",
            phoneNumber: "",
            memo: "",
            payMethod: "card",
            bank: "",
            account: "",
        },
    });

    const onModal = (text: string) => {
        setModalContent(<Alert>{text}</Alert>);
        setIconName("close");
        openModal();
    };

    useEffect(() => {
        if (data?.onlyUseAccount) {
            method.reset({
                ...method.getValues(),
                payMethod: data.onlyUseAccount === "true" ? "account" : "card",
            });
        }
    }, [data, method]);

    const onSubmit = async (submitData: PaymentFormInputProps) => {
        setPaidMethodState(method.watch().payMethod);

        if (handleCheckValid(submitData.terms)) {
            if (submitData.payMethod === "account" && submitData.bank === "") {
                onModal("입금 계좌를 선택해주세요.");
                return;
            } else {
                if (submitData.bank !== "") {
                    if (confirm("입금 신청을 진행하시겠습니까? 예약자명과 입금자")) onSubmitHandler(submitData);
                } else {
                    if (confirm("결제를 진행하시겠습니까?")) onSubmitHandler(submitData);
                }
            }
        }

        if (!handleCheckValid(submitData.terms)) onModal("약관에 모두 동의 해주세요.");
    };

    const handleCheckValid = (terms: { [key: string]: boolean }) => {
        const isAllChecked = Object.values(terms).every(Boolean);
        return isAllChecked;
    };

    return (
        <Flex maxW={"768px"} w="100%" flexDir="column" gap="24px">
            <FormProvider {...method}>
                <form onSubmit={method.handleSubmit(onSubmit)}>
                    <Flex width={"100%"} flexDir={"column"} gap="32px">
                        <PayerInfo
                            account={data?.account}
                            use={data?.use}
                            control={method.control}
                            onlyUseAccount={data?.onlyUseAccount}
                        />
                        <Terms control={method.control} />
                        <CustomButton type="submit">
                            {method.watch().payMethod == "account"
                                ? `무통장 입금 신청하기`
                                : `${totalPrice.toLocaleString()}원 결제하기`}
                        </CustomButton>
                    </Flex>
                </form>
            </FormProvider>
        </Flex>
    );
};

export const CustomButton = styled(Button)`
    background: ${({ theme }) => theme.COLOR.pink[200]};
    border: 1px solid ${({ theme }) => theme.COLOR.pink[200]};
    color: ${({ theme }) => theme.COLOR.white};
`;
